import React, { useCallback, useContext, useEffect, useState } from "react";
import PageActionContext from "@stores/page-action-context";
import AppDataContext from "@stores/app-data-context";
import FormEmploymentWidget from "./form-employment";
import OptionsBox from "@ui/options-box";
import { IconInfoPrimary } from "@ui/icons";
import { LinkDefault } from "@ui/links";

const UnemployedValue = "unemployed";

const InsufficientEmploymentHistoryMsg = ({ data, onClick }) => {
    const handleClick = (e) => {
        e.preventDefault();
        if (onClick) onClick();
    };

    return (
        <div className="flex gap-3 bg-skin-primary-light p-4 mt-8 rounded-md">
            <div className="text-white">
                <IconInfoPrimary />
            </div>
            <div>
                <h3 className="font-semibold text-skin-dark">{data.heading}</h3>
                <p className="mt-1">{data.text}</p>
                <LinkDefault
                    data={data.link}
                    onClick={handleClick}
                    className="mt-3"
                />
            </div>
        </div>
    );
};

const isUnemployed = (employmentType) => { return employmentType === UnemployedValue };

const ToEmploymentForContexStore = (form) => {
    if(isUnemployed(form.values.employmentType)) {
        return {
            employmentType: form.values.employmentType,
            years: 0,
            months: 0,
        };
    }

    return {
        employmentType: form.values.employmentType,
        years: parseInt(form.values.years, 10),
        months: parseInt(form.values.months, 10),
    };
};

const FormEmploymentDetails = ({ data }) => {
    const [sameCurrentAndPreviousEmployerIndustryOptionBox, setSameCurrentAndPreviousEmployerIndustryOptionBox] = useState({...data.sameCurrentAndPreviousEmployerIndustryOptionBox}); 
    const [currentEmployment, setCurrentEmployment] = useState({});
    const [previousEmployment, setPreviousEmployment] = useState({});
    const [showPreviousEmploymentForm, setShowPreviousEmploymentForm] = useState(false);
    const [showInsufficientEmploymentHistoryMsg, setShowInsufficientEmploymentHistoryMsg] = useState(false);
    const [isCurrentEmploymentFormValid, setCurrentEmploymentFormValid] = useState(false);
    const [isPreviousEmploymentFormValid, setPreviousEmploymentFormValid] = useState(true); 
    const [employmentIndustryContinuation, setEmploymentIndustryContinuation] = useState(null);
    const [isCurrentEmploymentDirty, setCurrentEmploymentDirty] = useState(false);
    const [isPreviousEmploymentDirty, setPreviousEmploymentDirty] = useState(false);    

    
    const loadFromContext = () => {
        const data = appDataCxt.getEmploymentDetails();
        if (data) {     

            if(data.employmentIndustryContinuation !== null)
            {
                setSameCurrentAndPreviousEmployerIndustryOptionBox({
                    value: data.employmentIndustryContinuation.toString(),
                    ...sameCurrentAndPreviousEmployerIndustryOptionBox});
            }              
            else            
            {
                setSameCurrentAndPreviousEmployerIndustryOptionBox({
                    value: null, 
                    ...sameCurrentAndPreviousEmployerIndustryOptionBox});
            }   
            if(data.currentEmployment)
            {
                setCurrentEmployment(data.currentEmployment);
                setCurrentEmploymentFormValid(true);
            }
            if(data.previousEmployment)
            {
                setPreviousEmployment(data.previousEmployment);
                setPreviousEmploymentFormValid(true);
            }
            if (data.previousEmployment?.employmentType && !isUnemployed(data.currentEmployment?.employmentType)) {
                setShowInsufficientEmploymentHistoryMsg(true);
                setShowPreviousEmploymentForm(true);
            }                     
        }        
    };
            
    const pageActionCxt = useContext(PageActionContext);
    const appDataCxt = useContext(AppDataContext);
    const cxtData = appDataCxt.getEmploymentDetails() ?? {};    

    const validateMonth = ({ value, others }) => {        
        if (isUnemployed(others.employmentType)) return true;
        if (value === "0") {
            return parseInt(others.years, 10) > 0;
        }
        return true;
    };

    const handleSubmit = (e) => {
        e?.preventDefault();
        if (pageActionCxt.isNextDisabled) return;                

        if(isCurrentEmploymentDirty || isPreviousEmploymentDirty) {
            appDataCxt.setEmploymentDetails({            
                employmentIndustryContinuation: (employmentIndustryContinuation === null? null: employmentIndustryContinuation === 'true'),
                currentEmployment,
                previousEmployment
            });
        }

        const nextEventData = pageActionCxt.nextEventData;

        if (nextEventData) {
            pageActionCxt.navigate(nextEventData.url, nextEventData.isExternal);
        }
    };

    useEffect(() => {
        pageActionCxt.reset();
        loadFromContext();
    }, []);

    useEffect(() => {
        if (pageActionCxt.isNextClicked) {
            handleSubmit(null);
            pageActionCxt.setNextClicked(false);
        }
    }, [pageActionCxt.isNextClicked]);
    
    const handleCurrentEmploymentFormChanged = useCallback((form) => {      
        
        setCurrentEmploymentDirty(form.dirty);
        
        if(isUnemployed(form.values.employmentType))
        {
            setShowInsufficientEmploymentHistoryMsg(false);    
            setShowPreviousEmploymentForm(false);            
        }

        if(!isUnemployed(form.values.employmentType))        
        {
            const isInsufficientEmploymentHistory = parseInt(form.values.years) === 0 && parseInt(form.values.months) < 6;

            setShowInsufficientEmploymentHistoryMsg(
                form.isValid && isInsufficientEmploymentHistory
            );

            if (!isInsufficientEmploymentHistory) 
            {
                setShowPreviousEmploymentForm(false);
                setPreviousEmployment({});
                setEmploymentIndustryContinuation(null);
            }
        }        
        
        setCurrentEmployment(ToEmploymentForContexStore(form));
        setCurrentEmploymentFormValid(form.isValid);
    });

    const handlePreviousEmploymentFormChanged = useCallback((form) => {

        setPreviousEmploymentDirty(form.dirty);

        if(form.isValid)   
        {   
            setPreviousEmployment(ToEmploymentForContexStore(form));         
            setPreviousEmploymentFormValid(form.isValid);            
        }

        if(!form.isValid && isUnemployed(form.values.employmentType)) {
            setPreviousEmployment(ToEmploymentForContexStore(form));
            setPreviousEmploymentFormValid(true);            
        }
    });

    const handleSameCurrentAndPreviousEmployerIndustryOptionSelected = (opt) => {                       
        if (opt === null) {
            setEmploymentIndustryContinuation(null);
        }
        else
        {
            setEmploymentIndustryContinuation(opt.value);
        }
    };

    const handleShowPreviousEmploymentClicked = () => {
        setShowPreviousEmploymentForm(true);
    };

    const disableNext = useCallback(() => {
        if (isUnemployed(currentEmployment.employmentType)) return false;
        if(!showInsufficientEmploymentHistoryMsg) return !(isCurrentEmploymentFormValid);

        return !(isCurrentEmploymentFormValid && isPreviousEmploymentFormValid && showPreviousEmploymentForm && employmentIndustryContinuation !== null);
    });                  

    pageActionCxt.setNextDisabled(disableNext());

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">            
            <FormEmploymentWidget             
                data={data.currentEmployment}
                initValues={cxtData?.currentEmployment ?? {}}
                onFormStateChanged={handleCurrentEmploymentFormChanged}
                validateMonth={validateMonth} 
            />

            {showInsufficientEmploymentHistoryMsg && (
                <InsufficientEmploymentHistoryMsg
                    data={data.insufficientEmploymentHistoryMsg}
                    onClick={handleShowPreviousEmploymentClicked}
                />
            )}

            {showPreviousEmploymentForm && (
                <>
                    <FormEmploymentWidget             
                        data={data.previousEmployment}
                        initValues={cxtData?.previousEmployment ?? {}}
                        onFormStateChanged={handlePreviousEmploymentFormChanged}
                        validateMonth={validateMonth}/>

                    <div>
                        <OptionsBox
                            data={sameCurrentAndPreviousEmployerIndustryOptionBox}
                            onOptionSelected={handleSameCurrentAndPreviousEmployerIndustryOptionSelected}
                        />
                    </div>
                </>
            )}

            <button type="submit" className="hidden">
                Submit
            </button>
        </form>
    );
};

export default FormEmploymentDetails;
